/**
 * Video Service - Handles API calls to the videos endpoint
 */

import api from "./api";

/**
 * Sync a video with the videos API
 * @param {Object} videoData - Video data to sync
 * @returns {Promise<Object>} Sync result
 */
export const syncVideo = async (videoData) => {
    try {
        console.log('Video sync triggered for category 8 (Video)');

        // Create FormData object for multipart/form-data request
        const formData = new FormData();

        // Add title and description
        if (videoData.title) formData.append('title', videoData.title);
        if (videoData.description) formData.append('description', videoData.description);
        if (videoData.post_id) formData.append('post_id', videoData.post_id);
        if (videoData.cover_image_url) formData.append('cover_image_url', videoData.cover_image_url);
        // Media information fields
        if (videoData.artist) formData.append('artist', videoData.artist);
        if (videoData.writer) formData.append('writer', videoData.writer);
        if (videoData.composer_name) formData.append('composer_name', videoData.composer_name);
        if (videoData.copyright) formData.append('copyright', videoData.copyright);
        if (videoData.links) formData.append('links', videoData.links);

        // Add video file
        if (videoData.video_file && videoData.video_file instanceof File) {
            formData.append('video_file', videoData.video_file);
        }

        // Use POST /sync to create video for existing post
        const response = await api.post('/videos/sync', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        console.log('Video sync successful');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error syncing video:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to sync video'
        };
    }
};

/**
 * Get streaming URL for a video
 * @param {number} videoId - Video ID
 * @returns {Promise<Object>} Stream data with URL and expiry
 */
export const getVideoStreamUrl = async (videoId) => {
    try {
        const response = await api.get(`/videos/${videoId}/stream`);
        return response.data;
    } catch (error) {
        console.error('Error getting video stream URL:', error);
        throw error;
    }
};

/**
 * Get streaming URL for a video
 * @param {number} videoId - Video ID
 * @returns {Promise<Object>} Stream data with URL and expiry
 */
export const getVideoStreamUrlForPosts = async (videoId) => {
    try {
        const response = await api.get(`/videos/post/${videoId}/stream`);
        return response.data;
    } catch (error) {
        console.error('Error getting video stream URL:', error);
        throw error;
    }
};

/**
 * Get video details
 * @param {number} videoId - Video ID
 * @returns {Promise<Object>} Video details
 */
export const getVideoDetails = async (videoId) => {
    try {
        const response = await api.get(`/videos/${videoId}`);
        return response.data;
    } catch (error) {
        console.error('Error getting video details:', error);
        throw error;
    }
};

/**
 * Get video details
 * @param {number} postId - post ID
 * @returns {Promise<Object>} Video details
 */
export const getVideoDetailsByPostId = async (postId) => {
    try {
        const response = await api.get(`/videos/post/${postId}`);
        return response.data;
    } catch (error) {
        if (error.response?.status === 404) {
            console.log('Error getting video details, video not found for post ID:', postId);
            return null;
        }else{
            console.error('Error getting video details:', error);
        }

    }
};

/**
 * List videos with pagination and filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Videos list with pagination
 */
export const listVideos = async (params = {}) => {
    try {
        const response = await api.get('/videos/', { params });
        return response.data;
    } catch (error) {
        console.error('Error listing videos:', error);
        throw error;
    }
};

/**
 * Update video metadata (without uploading a new file)
 * @param {number} postId - Post ID
 * @param {Object} metadata - Metadata to update
 * @returns {Promise<Object>} Update result
 */
export const updateVideoMetadata = async (postId, metadata) => {
    try {
        console.log('Updating video metadata for post:', postId);

        const formData = new FormData();
        if (metadata.title) formData.append('title', metadata.title);
        if (metadata.description) formData.append('description', metadata.description);
        if (metadata.cover_image_url) formData.append('cover_image_url', metadata.cover_image_url);
        // Media information fields
        if (metadata.artist) formData.append('artist', metadata.artist);
        if (metadata.writer) formData.append('writer', metadata.writer);
        if (metadata.composer_name) formData.append('composer_name', metadata.composer_name);
        if (metadata.copyright) formData.append('copyright', metadata.copyright);
        if (metadata.links) formData.append('links', metadata.links);

        const response = await api.put(`/videos/${postId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        console.log('Video metadata update successful');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating video metadata:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to update video metadata'
        };
    }
};

export const deleteVideo = async (videoid) => {
    try {
        await api.delete(`/videos/${videoid}`);
        return true;
    } catch (error) {
        console.log('Failed to delete post');
    }
};


// Export all functions as default object for easier importing
const videoService = {
    syncVideo,
    getVideoStreamUrl,
    getVideoStreamUrlForPosts,
    getVideoDetails,
    listVideos,
    updateVideoMetadata,
    getVideoDetailsByPostId,
    deleteVideo
};

export default videoService;
